export function parseExpiryTimestamp(value) {
  if (value == null || value === '') {
    return null;
  }

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}

export function getRetailPrice(item) {
  const raw = item?.brutto_retail_price;
  if (raw == null || raw === '') {
    return Infinity;
  }
  const price = Number(raw);
  return Number.isFinite(price) ? price : Infinity;
}

/**
 * Compare two inventory/batch rows by FEFO:
 * earliest expiry first, then lowest price, then stable id.
 */
export function compareByFefo(a, b) {
  const expiryA = parseExpiryTimestamp(a?.expiry);
  const expiryB = parseExpiryTimestamp(b?.expiry);

  if (expiryA != null && expiryB != null && expiryA !== expiryB) {
    return expiryA - expiryB;
  }
  if (expiryA != null && expiryB == null) {
    return -1;
  }
  if (expiryA == null && expiryB != null) {
    return 1;
  }

  const priceA = getRetailPrice(a);
  const priceB = getRetailPrice(b);
  if (priceA !== priceB) {
    return priceA - priceB;
  }

  return String(a?.id ?? a?.value ?? '').localeCompare(String(b?.id ?? b?.value ?? ''));
}

/**
 * Pick the best inventory/batch row from candidates using FEFO rules.
 */
export function selectByFefo(candidates, options = {}) {
  const { requireAvailable = false, excludeIds = [] } = options;
  const excluded = new Set((excludeIds || []).map(String));

  let pool = (candidates || []).filter(Boolean);

  if (requireAvailable) {
    pool = pool.filter((item) => item.availability !== false && !item.disabled);
  }

  pool = pool.filter((item) => {
    if (item.id != null && excluded.has(String(item.id))) {
      return false;
    }
    return true;
  });

  if (!pool.length) {
    return null;
  }

  return pool.reduce((best, current) => (compareByFefo(current, best) < 0 ? current : best));
}

function resolveBatchItemForOption(option, batches) {
  if (!option?.value) {
    return null;
  }

  const optionValue = String(option.value);

  for (const batch of batches || []) {
    const matchingItem = batch.items?.find((item) => {
      const itemId = item.id || item.tmp_id;
      return itemId && String(itemId) === optionValue;
    });

    if (matchingItem) {
      return matchingItem;
    }
  }

  if (option.expiry != null || option.brutto_retail_price != null) {
    return {
      id: option.value,
      expiry: option.expiry,
      brutto_retail_price: option.brutto_retail_price,
    };
  }

  return { id: option.value };
}

/**
 * Select a batch option value using FEFO among dropdown options.
 */
export function selectBatchOptionByFefo(options, batches) {
  if (!options?.length) {
    return null;
  }

  const sorted = [...options].sort((a, b) => {
    const itemA = resolveBatchItemForOption(a, batches);
    const itemB = resolveBatchItemForOption(b, batches);
    return compareByFefo(itemA, itemB);
  });

  return sorted[0]?.value ?? null;
}

/**
 * Brutto retail price of the FEFO batch among inventory rows (available rows first).
 */
export function getFefoBruttoRetailPrice(candidates, options = {}) {
  const available = (candidates || []).filter((item) => item?.availability === true);
  const selected = selectByFefo(available.length ? available : candidates, options);

  if (!selected) {
    return 0;
  }

  const price = Number(selected.brutto_retail_price);
  return Number.isFinite(price) ? price : 0;
}
